/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AgentChatBotResponse } from '../services/supplier-discovery.service';
import { BaseApi } from 'sustainment-component';
import { environment } from 'src/environments/environment';
import { camelizeKeys } from '../utils/rxjsUtils';
import { odataQuery } from 'odata-fluent-query';

@Injectable()
export class AgentsApi extends BaseApi {
  protected urlBase: string;
  private readonly _assistantId = 'e1728a1f-45b3-4c5a-8873-5cd445dde9c2';

  public constructor(_http: HttpClient) {
    super(_http);
    this.urlBase = environment.agentsApiBaseUrl;
  }

  public startNewSupplierDiscoveryConversation(
    initialMessage: string
  ): Observable<AgentChatBotResponse> {
    return this.post<AgentChatBotResponse>(
      `/Agents(${this._assistantId})/StartConversation()`,
      { content: initialMessage }
    ).pipe(camelizeKeys());
  }

  public createConversationWithFiles(): Observable<{ value: string }> {
    return this.post<{ value: string }>(
      `/Agents(${this._assistantId})/CreateConversationWithFiles()`,
      {}
    );
  }

  public startConversationWithFiles(
    conversationId: string,
    content: string
  ): Observable<AgentChatBotResponse> {
    return this.post<AgentChatBotResponse>(
      `/Conversations(${conversationId})/StartWithFiles()`,
      { content }
    ).pipe(camelizeKeys());
  }

  public sendSupplierDiscoveryMessage(
    conversationId: string,
    content: string
  ): Observable<AgentChatBotResponse> {
    return this.post<AgentChatBotResponse>(
      `/Conversations(${conversationId})/AddMessage()`,
      { content }
    ).pipe(camelizeKeys());
  }

  public uploadFile(conversationId: string, file: File): Observable<boolean> {
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post<boolean>(
      environment.agentsApiBaseUrl.replace('v1', 'api') +
        `/files/${conversationId}`,
      formData
    );
  }

  public getUserCurrentMessageLimit(
    date1: Date,
    date2: Date,
    userId: string
  ): Observable<number> {
    const query = odataQuery<AgentsSupplierDiscoveryMessage>()
      .filter((msg) =>
        msg.DateCreated.isAfter(date1.toISOString())
          .and(msg.DateCreated.isBefore(date2.toISOString()))
          .and(msg.Conversation.CreatedById.equals(`${userId}`))
          .and(msg.Role.equals('User'))
      )
      .toString();

    return this.get(`/Messages/$count?${query}`);
  }
}

// AVJ: These types are temporary until we have TS generation from our OData services ready
export type AgentsSupplierDiscoveryMessage = {
  /**
   * Gets or sets the unique identifier for the message.
   */
  Id: string;
  /**
   * Gets or sets the ID of the conversation this message belongs to.
   */
  ConversationId: string;
  /**
   * Gets or sets the content of the message.
   */
  Content: string;
  /**
   * Gets or sets the role of the message sender (user or assistant).
   */
  Role: string;
  /**
   * Gets or sets a value indicating whether this message has any file attachments.
   */
  HasAttachments: boolean;
  /**
   * Date when the message was created
   */
  DateCreated: Date;
  /**
   * The conversation the message belongs to.
   */
  Conversation: AgentsSupplierDiscoveryConversation;
};

export type AgentsSupplierDiscoveryConversation = {
  Id: string;
  AgentId: string;
  DisplayName: string;
  ProviderThreadId: string;
  IsActive: boolean;
  CreatedById: string;
  DateCreated: Date;
  DateUpdated?: Date | null;
};
